import { useEffect, useRef, useState } from 'react';
import {
    Chart as ChartJS,
    ChartOptions,
    Filler,
    Legend,
    LineElement,
    PointElement,
    RadialLinearScale,
    ScriptableContext,
    Tooltip,
} from 'chart.js';
import 'chartjs-plugin-dragdata';
import { Radar } from 'react-chartjs-2';
import { GEAR_SCORE_CATEGORIES } from '@/constants/constants';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface GearScoreProps {
    scores: {
        score_software: number;
        score_hardware: number;
        score_daily: number;
        score_innovation: number;
        score_specialized: number;
        score_versatile: number;
    };
    onScoreChange?: (newScores: any) => void;
    dragEnabled?: boolean;
    showScores?: boolean;
    onAnimationComplete?: () => void;
    isDark?: boolean;
}

export default function GearScore({
    scores,
    onScoreChange,
    dragEnabled = false,
    showScores = false,
    onAnimationComplete,
    isDark = false,
}: GearScoreProps) {
    const [chartData, setChartData] = useState({
        labels: [] as string[],
        datasets: [
            {
                label: '',
                data: [] as number[],
                backgroundColor: (context: ScriptableContext<'radar'>) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) {
                        return null as any;
                    }
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(0, 98, 189, 0.9)');
                    gradient.addColorStop(1, 'rgba(40, 255, 255, 0.4)');
                    return gradient;
                },
                borderColor: (context: ScriptableContext<'radar'>) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) {
                        return null as any;
                    }
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(0, 98, 189, 1)');
                    gradient.addColorStop(1, 'rgba(40, 255, 255, 1)');
                    return gradient;
                },
                borderWidth: 2,
                pointRadius: (ctx: ScriptableContext<'radar'>) => {
                    const chartWidth = ctx.chart.width;
                    if (chartWidth > 500) {
                        return 8;
                    } else if (chartWidth > 300) {
                        return 6;
                    }
                    return 4;
                },
                pointBackgroundColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(255, 159, 64, 1)',
                ],
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
            },
        ],
    });

    const chartRef = useRef<ChartJS<'radar'>>(null);

    useEffect(() => {
        const sortedCategories = [...GEAR_SCORE_CATEGORIES].sort((a, b) => a.graphIndex - b.graphIndex);

        const updatedScores = sortedCategories.map((category) => {
            const score = scores[category.field as keyof typeof scores];
            return score !== undefined && score !== null ? Math.max(1, Math.min(5, score)) : 1;
        }) as number[];

        setChartData({
            labels: sortedCategories.map((category) => category.label),
            datasets: [
                {
                    ...chartData.datasets[0],
                    data: updatedScores,
                },
            ],
        });
    }, [scores]);

    const textColor = isDark ? '#FFFFFF' : '#1D284D';

    const outerGridColor = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,1)';

    const gridColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';

    const angleLineColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)';

    const options: ChartOptions<'radar'> = {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
            r: {
                min: 0,
                max: 5,
                ticks: {
                    stepSize: 1,
                    display: false,
                },
                pointLabels: {
                    font: (ctx) => {
                        const chartWidth = ctx.chart.width;
                        return {
                            size: chartWidth <= 450 ? 12 : 16,
                            family: 'Corporate Logo, sans-serif',
                            weight: 700,
                        };
                    },
                    color: textColor,
                    padding: 8,
                    callback: (label: string, index: number) => {
                        if (!showScores) return label;

                        const sortedCategories = [...GEAR_SCORE_CATEGORIES].sort((a, b) => a.graphIndex - b.graphIndex);

                        const field = sortedCategories[index].field as keyof typeof scores;
                        const score = scores[field];

                        return [label, String(score)];
                    },
                },
                grid: {
                    color: (ctx) => {
                        const index = ctx.tick?.value;
                        return index === 5 ? outerGridColor : gridColor;
                    },
                    lineWidth: (ctx) => {
                        const index = ctx.tick?.value;
                        return index === 5 ? 3 : 1;
                    },
                },
                angleLines: {
                    color: angleLineColor,
                    lineWidth: 1,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
            },
            dragData:
                dragEnabled && onScoreChange
                    ? {
                          onDrag: function (e: any, datasetIndex: number, index: number, value: number | null) {
                              if (value === null || value === undefined) return;

                              value = Math.max(1, Math.min(5, Math.round(value)));

                              const sortedCategories = [...GEAR_SCORE_CATEGORIES].sort(
                                  (a, b) => a.graphIndex - b.graphIndex,
                              );

                              const field = sortedCategories[index].field as keyof typeof scores;

                              const updatedData = chartData.datasets[0].data.map((v, i) =>
                                  i === index ? value ?? 0 : v,
                              );

                              setChartData({
                                  ...chartData,
                                  datasets: [{ ...chartData.datasets[0], data: updatedData }],
                              });

                              if (onScoreChange) {
                                  const updatedScores = { ...scores };
                                  updatedScores[field] = value;
                                  onScoreChange(updatedScores);
                              }
                          },
                      }
                    : false,
        },
        animation: {
            duration: 1500,
            easing: 'easeOutCirc',
            loop: false,
            onComplete: onAnimationComplete ? () => onAnimationComplete() : undefined,
        },
        onHover: (event, chartElement) => {
            if (event.native && event.native.target) {
                const target = event.native.target as HTMLElement;
                target.style.cursor = chartElement.length ? 'pointer' : 'default';
            }
        },
    };

    return (
        <div
            className="flex justify-center w-full max-w-[400px] md:max-w-[450px] aspect-square"
            style={{
                pointerEvents: dragEnabled ? 'auto' : 'none',
            }}
        >
            <Radar ref={chartRef} data={chartData} options={options} />
        </div>
    );
}
